import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  createQColumnHelper,
  DataProvider,
  QBox,
  QCard,
  QCenter,
  QCollapse,
  QDataTable,
  QHeading,
  QIcon,
  QIconButton,
  QStack,
  QTag,
  QTooltip,
} from "@qualio/ui-components";

import { FeatureFlags, TestCaseDesignElement } from "@design-controls/types";
import { useCurrentConfigs } from "../../hooks/useCurrentConfigs";
import { useCurrentProduct } from "../../hooks/useCurrentProduct";
import { Link, useNavigate } from "react-router-dom";
import { useFlags } from "launchdarkly-react-client-sdk";
import { designElementStatusColumn } from "../DesignElementStatusColumn/DesignElementStatusColumn";
import { sortByCode } from "../../sortByCode";

const columnHelper = createQColumnHelper<TestCaseDesignElement>();

type DesignElement = { latest?: string } & TestCaseDesignElement;

type Props = {
  testCases: Array<DesignElement>;
  underReview?: boolean;
  versioned?: boolean;
};

export const TestCaseTable: React.FC<Props> = ({
  testCases,
  underReview,
  versioned = false,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(true);
  const configs = useCurrentConfigs();
  const navigate = useNavigate();
  const features = useFlags();
  const { id: productId } = useCurrentProduct();

  const onClickToDiff = useCallback(
    (item: DesignElement) => {
      return () => navigate(`${item.id}`);
    },
    [navigate],
  );

  const columns = useMemo(() => {
    const columns = [
      columnHelper.code("code", {
        header: t("testCases.list.table.header.id"),
        minWidth: "100px",
        width: "10%",
      }),
      {
        accessorKey: "title",
        header: t("testCases.list.table.header.title"),
        meta: {
          minWidth: "300px",
          width: "30%",
        },
        cell: ({
          row: { original: row },
        }: {
          row: { original: DesignElement };
        }) => {
          return (
            <Link
              to={`/product/${productId}/test-case/${row.id}${
                versioned ? `/version/${row.version}` : ""
              }`}
              target="_blank"
            >
              {row.title}
            </Link>
          );
        },
      },
      columnHelper.text((record) => record.category?.label, {
        header: t("testCases.list.table.header.category"),
        minWidth: "150px",
        width: "20%",
      }),
      {
        header: t("testCases.list.table.header.type"),
        meta: {
          minWidth: "150px",
          width: "10%",
        },
        cell: ({
          row: { original: row },
        }: {
          row: { original: TestCaseDesignElement };
        }) => {
          return (
            <QTag variantColor="gray">
              {
                (
                  configs.find((config) => config.type === row.type) as
                    | { label: string }
                    | undefined
                )?.label
              }
            </QTag>
          );
        },
      },
      {
        ...designElementStatusColumn(),
        meta: {
          minWidth: "150px",
          width: "10%",
        },
      },
    ];

    if (versioned && features[FeatureFlags.COMPARE_VIEW]) {
      columns.push({
        header: " ",
        id: "diff",
        meta: {
          minWidth: "50px",
          width: "10%",
        },
        cell: ({
          row: { original: row },
        }: {
          row: { original: DesignElement };
        }) => {
          return (
            row.itemStatus !== "NEW" && (
              <QCenter>
                <QTooltip label={t("common.ariaLabels.seeDifference")}>
                  <QBox>
                    <QIconButton
                      iconName="GitPullRequest"
                      aria-label={t(
                        "common.ariaLabels.seeDifferenceBetweenVersion",
                      )}
                      onClick={onClickToDiff(row)}
                    />
                  </QBox>
                </QTooltip>
              </QCenter>
            )
          );
        },
      });
    }

    if (underReview) {
      columns.push({
        header: " ",
        meta: {
          minWidth: "90px",
          width: "10%",
        },
        cell: ({
          row: { original: row },
        }: {
          row: { original: DesignElement };
        }) => {
          return row.latest && row.version !== row.latest ? (
            <QCenter>
              <QIcon
                iconName="FillExclamationTriangleFill"
                color="orange.500"
              />
            </QCenter>
          ) : null;
        },
      });
    }

    return columns;
  }, [underReview, versioned, onClickToDiff, features, t]);

  if (!testCases.length) {
    return null;
  }

  return (
    <QCard>
      <QStack
        direction="row"
        justifyContent="space-between"
        padding="12px 16px"
      >
        <QHeading fontSize="md" fontWeight={600}>
          {t("testCases.policy.heading")}
        </QHeading>
        <QIconButton
          onClick={() => setExpanded((s) => !s)}
          iconName={expanded ? "ChevronUp" : "ChevronDown"}
          aria-label={
            expanded
              ? t("testCases.list.collapseExpand.collapse")
              : t("testCases.list.collapseExpand.expand")
          }
        />
      </QStack>
      <QCollapse isOpen={expanded}>
        <DataProvider.Fixed data={testCases.sort(sortByCode)}>
          <QDataTable
            columns={columns}
            hideItemCount
            data-cy="test-case-list"
          />
        </DataProvider.Fixed>
      </QCollapse>
    </QCard>
  );
};
